import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Checkbox,
  FormControlLabel,
  Card,
  CardContent,
  Tooltip,
  useMediaQuery,
  useTheme,
  Stack,
  Collapse,
  Tabs,
  Tab
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PdfIcon,
  VisibilityOutlined as ViewPdfIcon,
  Lock as FinalizeIcon,
  Undo as ReturnDraftIcon,
  RequestPage as CreditNoteIcon,
  Cancel as VoidIcon,
  Payment as PaidIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  TrendingUp as MarkupIcon,
  TrendingDown as DiscountIcon,
  RemoveCircleOutline as RemoveMarkupIcon,
  Email as EmailIcon,
  Send as SendIcon,
  History as HistoryIcon,
  Check as SaveIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { can } from '../utils/roleUtils';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import ApiService from '../services/api.service';
import useThaklisServicesOff from '../hooks/useThaklisServicesOff';
import AddExternalServiceDialog from '../components/AddExternalServiceDialog';
import CreditNoteDialog, { COMPONENT_LABEL } from './CreditNoteDialog';
import InvoiceFeeDialog, { isDirectFeeLine } from './InvoiceFeeDialog';
import { PaidItemOutcomes, outcomesComplete, outcomesBody } from './work-orders/WorkOrderPaymentDialog';
import { needsOutcome, EVIDENCE } from '../components/accounting/PaymentSourceFields';
import { StatusChip } from '../components/accounting';
import { invoiceMoneyService } from '../services/accounting/invoiceMoney.service';
import { serverMessage, unwrapList } from '../utils/apiConvert';
import { transactionNumbersOf } from '../utils/transactionNumbers';

// A work order line's add-ons (and an external line's fine) sit outside line_total, as on the work order.
const rowCharges = (item) => {
  const outsideFine = item.is_external && item.fine ? Number(item.fine) : 0;
  const addon = Number(item.addon_amount || 0);
  const addonVat = Number(item.addon_vat || 0);
  return {
    fine: Number(item.fine || 0), addon, addonVat,
    total: Number(item.line_total || 0) + outsideFine + addon + addonVat,
  };
};

// Fees = government fee + fine + work order add-ons, shown as one figure (add-ons are never named on the invoice);
// the add-ons' VAT shows in the Tax column. Display only - the stored amounts and totals are unchanged.
const lineFees = (item) => Number(item.govt_charge || 0) + Number(item.fine || 0) + Number(item.addon_amount || 0);
const hasFeeExtras = (items) => (items || []).some((i) => Number(i.fine || 0) > 0 || Number(i.addon_amount || 0) > 0);

// The Tax Details and Subtotal & Tax switches remember their last values across all invoices.
const PDF_PREFS_KEY = 'invoicePdfPrefs';
const loadPdfPrefs = () => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(PDF_PREFS_KEY) || 'null');
    return { includeTaxDetails: saved?.includeTaxDetails !== false, showSubtotalTax: saved?.showSubtotalTax === true };
  } catch {
    return { includeTaxDetails: true, showSubtotalTax: false };
  }
};
const savePdfPrefs = (prefs) => {
  try {
    window.localStorage.setItem(PDF_PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // Storage blocked: the switches simply aren't remembered.
  }
};

const InvoiceDetail = ({ currentUser }) => {
  const navigate = useNavigate();
  const { invoiceId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(true);
  const thaklisServicesOff = useThaklisServicesOff(currentUser);
  const [error, setError] = useState(null);
  const [reordering, setReordering] = useState(false);
  const [success, setSuccess] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});

  // Dialog states
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
  const [voidDialogOpen, setVoidDialogOpen] = useState(false);
  const [markupDialogOpen, setMarkupDialogOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [markupAmount, setMarkupAmount] = useState('');
  const [applyingMarkup, setApplyingMarkup] = useState(false);
  const [removingMarkup, setRemovingMarkup] = useState(false);

  // Line item markup states
  const [lineItemMarkupDialogOpen, setLineItemMarkupDialogOpen] = useState(false);
  const [selectedLineItem, setSelectedLineItem] = useState(null);
  const [lineItemMarkupAmount, setLineItemMarkupAmount] = useState('');
  // 'markup' (positive) or 'discount' (sent as negative markupAmount on save)
  const [markupDialogTab, setMarkupDialogTab] = useState('markup');
  const [applyingLineItemMarkup, setApplyingLineItemMarkup] = useState(false);

  // Line item customer-name edit
  const [customerEditOpen, setCustomerEditOpen] = useState(false);
  const [customerEditItem, setCustomerEditItem] = useState(null);
  const [customerEditValue, setCustomerEditValue] = useState('');
  const [descriptionEditValue, setDescriptionEditValue] = useState('');
  const [transactionNumberEditValue, setTransactionNumberEditValue] = useState('');
  const [quantityEditValue, setQuantityEditValue] = useState('1');
  const [govtChargeEdit, setGovtChargeEdit] = useState('');
  const [serviceChargeInclEdit, setServiceChargeInclEdit] = useState('');
  const [serviceChargeExclEdit, setServiceChargeExclEdit] = useState('');
  const [taxEdit, setTaxEdit] = useState('');
  const [savingCustomerName, setSavingCustomerName] = useState(false);

  // Invoice money: direct-line fee payments, credit notes, partner credit notes not passed on yet (BE-3d, BE-20).
  const [feePayments, setFeePayments] = useState([]);
  const [creditNotes, setCreditNotes] = useState([]);
  const [partnerPrompts, setPartnerPrompts] = useState([]);
  const [feeLineId, setFeeLineId] = useState(null);
  const [creditOpen, setCreditOpen] = useState(false);
  const [creditPrefill, setCreditPrefill] = useState(null);
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnReason, setReturnReason] = useState('');
  const [returning, setReturning] = useState(false);
  const [outcomes, setOutcomes] = useState({});
  const [removeTarget, setRemoveTarget] = useState(null);
  const [tcAccounts, setTcAccounts] = useState([]);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Ticket selection
  const [availableTickets, setAvailableTickets] = useState([]);
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [invoicedTicketIds, setInvoicedTicketIds] = useState([]);

  // Action loading states
  const [addingTickets, setAddingTickets] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [viewingPdf, setViewingPdf] = useState(false);
  const [includeTaxDetails, setIncludeTaxDetails] = useState(() => loadPdfPrefs().includeTaxDetails);
  // With tax details off, still show the Subtotal and Tax totals (page and PDF).
  const [showSubtotalTax, setShowSubtotalTax] = useState(() => loadPdfPrefs().showSubtotalTax);
  // Invoice config drives the line-item table: the service-charge column label
  // and whether the "Customer" column is shown (mirrors the PDF).
  const [invoiceConfig, setInvoiceConfig] = useState(null);

  // Email dialog states
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState([]);
  const [emailCc, setEmailCc] = useState('');
  const [emailHistory, setEmailHistory] = useState([]);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [loadingEmailHistory, setLoadingEmailHistory] = useState(false);

  // External service dialog state
  const [externalServiceDialogOpen, setExternalServiceDialogOpen] = useState(false);

  // Update invoice dialog state
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [updateInvoiceDateInput, setUpdateInvoiceDateInput] = useState(null);
  const [updateSubjectInput, setUpdateSubjectInput] = useState('');
  const [savingUpdate, setSavingUpdate] = useState(false);

  const [typingCenterOrgType, setTypingCenterOrgType] = useState(null);
  const [internalOrganizations, setInternalOrganizations] = useState([]);
  const [loadingInternalOrgs, setLoadingInternalOrgs] = useState(false);
  const [selectedInternalOrgId, setSelectedInternalOrgId] = useState('');
  // Full selected org object + the search text — the picker is a searchable
  // Autocomplete that queries the backend by name.
  const [selectedInternalOrg, setSelectedInternalOrg] = useState(null);
  const [internalOrgSearch, setInternalOrgSearch] = useState('');

  // Customer Payment History (receipts for this invoice)
  const [paymentReceipts, setPaymentReceipts] = useState([]);
  const [loadingReceipts, setLoadingReceipts] = useState(false);
  const [selectedReceiptIds, setSelectedReceiptIds] = useState([]);
  const [bulkVoidDialogOpen, setBulkVoidDialogOpen] = useState(false);
  const [bulkVoidReason, setBulkVoidReason] = useState('');
  const [bulkVoidProcessing, setBulkVoidProcessing] = useState(false);

  useEffect(() => {
    fetchInvoice();
    fetchReceipts();
    loadMoney();
  }, [invoiceId]);

  useEffect(() => {
    ApiService.getTcBankAccounts().then(unwrapList).then((r) => setTcAccounts(r.items)).catch(() => {});
  }, []);

  const loadMoney = async () => {
    const [fees, notes, prompts] = await Promise.all([
      invoiceMoneyService.getFeePayments(invoiceId).catch(() => []),
      invoiceMoneyService.getCreditNotes(invoiceId).catch(() => []),
      invoiceMoneyService.getPartnerPrompts(invoiceId).catch(() => []),
    ]);
    setFeePayments(fees);
    setCreditNotes(notes);
    setPartnerPrompts(prompts);
  };

  const refreshMoney = async () => { await loadMoney(); fetchInvoice(true); };

  // Fee payments of the TC's own money that need an outcome when the line or invoice goes away.
  const paidFees = (lineId) => feePayments.filter((fp) => needsOutcome(fp) && (!lineId || fp.lineItemId === lineId));

  const fetchInvoice = async (silent = false) => {
    if (silent !== true) setLoading(true);
    try {
      const response = await ApiService.getInvoiceById(invoiceId);
      if (response.data?.data) {
        const inv = response.data.data;
        setInvoice(inv);
        // Load the TC's invoice config so the table matches the PDF (svc-charge
        // label + customer column). Non-fatal: defaults apply if it fails.
        const tcId = inv.typing_center_id || inv.typingCenterId;
        if (tcId) {
          try {
            const cfgRes = await ApiService.getInvoiceConfig(tcId);
            setInvoiceConfig(cfgRes.data?.data || null);
          } catch (cfgErr) {
            console.warn('Failed to load invoice config for detail table:', cfgErr);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching invoice:', err);
      setError('Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const fetchReceipts = async () => {
    if (!invoiceId) return;
    setLoadingReceipts(true);
    try {
      const ReceiptService = (await import('../services/receipt.service')).default;
      const list = await ReceiptService.getByInvoice(invoiceId);
      setPaymentReceipts(Array.isArray(list) ? list : []);
    } catch (err) {
      // non-fatal — block just shows empty
      console.warn('Failed to load receipts for invoice:', err);
      setPaymentReceipts([]);
    } finally {
      setLoadingReceipts(false);
      setSelectedReceiptIds([]);
    }
  };

  const toggleReceiptSelection = (id) => {
    setSelectedReceiptIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAllActiveReceipts = (checked) => {
    if (checked) {
      const activeIds = paymentReceipts
        .filter((r) => r.status !== 'VOIDED')
        .map((r) => r.id);
      setSelectedReceiptIds(activeIds);
    } else {
      setSelectedReceiptIds([]);
    }
  };

  const openBulkVoidDialog = () => {
    if (selectedReceiptIds.length === 0) return;
    setBulkVoidReason('');
    setBulkVoidDialogOpen(true);
  };

  const handleBulkVoidSubmit = async () => {
    if (!bulkVoidReason.trim()) {
      setError('Please provide a reason for voiding the selected receipt(s)');
      return;
    }
    setBulkVoidProcessing(true);
    setError(null);
    try {
      const ReceiptService = (await import('../services/receipt.service')).default;
      const failures = [];
      for (const id of selectedReceiptIds) {
        try {
          await ReceiptService.void(id, bulkVoidReason.trim());
        } catch (err) {
          const msg = err?.response?.data?.error || err?.message || 'Unknown error';
          failures.push({ id, msg });
        }
      }
      setBulkVoidDialogOpen(false);
      if (failures.length > 0) {
        const successCount = selectedReceiptIds.length - failures.length;
        setError(
          `${successCount} receipt(s) voided. ${failures.length} failed: ${failures
            .map((f) => f.msg)
            .join('; ')}`
        );
      } else {
        setSuccess(`${selectedReceiptIds.length} receipt(s) voided successfully`);
      }
      await fetchInvoice();
      await fetchReceipts();
    } finally {
      setBulkVoidProcessing(false);
    }
  };

  const normalizeInternalOrganization = (item) => {
    if (!item) return null;
    return {
      id: item.id,
      typingCenterId: item.typingCenterId ?? item.typing_center_id,
      name: item.name ?? '',
      address: item.address ?? '',
      contactPerson: item.contactPerson ?? item.contact_person ?? '',
      contactNumber: item.contactNumber ?? item.contact_number ?? '',
      email: item.email ?? ''
    };
  };

  const fetchTypingCenterOrgType = async (tcId) => {
    if (!tcId) {
      setTypingCenterOrgType(null);
      return;
    }
    try {
      const response = await ApiService.get(`/api/typing-centers/${tcId}/with-mappings`);
      if (response.data && response.data.success) {
        const centerData = response.data.data;
        const orgType = centerData?.organization_type || centerData?.organizationType || null;
        setTypingCenterOrgType(orgType);
      } else {
        setTypingCenterOrgType(null);
      }
    } catch (err) {
      console.error('Error fetching typing center org type:', err);
      setTypingCenterOrgType(null);
    }
  };

  const fetchInternalOrganizations = async (tcId, search) => {
    if (!tcId) {
      setInternalOrganizations([]);
      return;
    }

    try {
      setLoadingInternalOrgs(true);
      const params = { typingCenterId: tcId, page: 0, size: 200 };
      if (search && search.trim()) params.search = search.trim();
      const response = await ApiService.getInternalOrganizationsPage(params);

      if (response.data && response.data.success) {
        const list = Array.isArray(response.data.data?.content) ? response.data.data.content : [];
        setInternalOrganizations(list.map(normalizeInternalOrganization).filter(Boolean));
      } else if (Array.isArray(response.data)) {
        setInternalOrganizations(response.data.map(normalizeInternalOrganization).filter(Boolean));
      } else {
        setInternalOrganizations([]);
      }
    } catch (err) {
      console.error('Error fetching internal organizations:', err);
      setInternalOrganizations([]);
    } finally {
      setLoadingInternalOrgs(false);
    }
  };

  useEffect(() => {
    const tcId = invoice?.typing_center_id || invoice?.typingCenterId;
    fetchTypingCenterOrgType(tcId);
  }, [invoice?.typing_center_id, invoice?.typingCenterId]);

  const updatePdfPrefs = (change) => {
    const next = { includeTaxDetails, showSubtotalTax, ...change };
    setIncludeTaxDetails(next.includeTaxDetails);
    setShowSubtotalTax(next.showSubtotalTax);
    savePdfPrefs(next);
  };

  const showSubtotalAndTax = includeTaxDetails || showSubtotalTax;
  const pdfParams = includeTaxDetails
    ? {}
    : { includeTaxDetails: false, ...(showSubtotalTax ? { showSubtotalTax: true } : {}) };

  useEffect(() => {
    if (!updateDialogOpen) return;
    if (!invoice) return;
    if (selectedInternalOrgId) return;
    if (!Array.isArray(internalOrganizations) || internalOrganizations.length === 0) return;

    // Prefill directly from the invoice's internal_organization_id if available.
    const currentInternalOrgId = invoice?.internal_organization_id || invoice?.internalOrganizationId;
    const match = currentInternalOrgId && internalOrganizations.find((o) => o.id === currentInternalOrgId);
    if (match) {
      setSelectedInternalOrgId(currentInternalOrgId);
      setSelectedInternalOrg(match);
    }
  }, [updateDialogOpen, internalOrganizations, invoice, selectedInternalOrgId]);

  // Searchable picker: (re)load internal orgs from the backend whenever the dialog is open
  // and the search text changes (debounced). The backend sorts by name and filters by name.
  useEffect(() => {
    if (!updateDialogOpen) return;
    if (!(isEditable && ['TYPING_CENTER', 'B2B'].includes(typingCenterOrgType))) return;
    const tcId = invoice?.typing_center_id || invoice?.typingCenterId;
    if (!tcId) return;
    const t = setTimeout(() => fetchInternalOrganizations(tcId, internalOrgSearch), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateDialogOpen, internalOrgSearch, typingCenterOrgType]);

  const fetchAvailableTickets = async () => {
    if (!invoice?.organization_id) return;

    setLoadingTickets(true);
    try {
      // First get already invoiced ticket IDs
      const invoicedResponse = await ApiService.getInvoicedTicketIds(invoice.organization_id);
      const invoicedIds = invoicedResponse.data?.data || [];
      setInvoicedTicketIds(invoicedIds);

      // Fetch tickets for this organization
      const ticketResponse = await ApiService.getTickets({
        organizationId: invoice.organization_id,
        status: 'COMPLETED'
      });

      if (ticketResponse.data?.data) {
        // Filter out already invoiced tickets
        const available = ticketResponse.data.data.filter(
          t => !invoicedIds.includes(t.id)
        );
        setAvailableTickets(available);
      }
    } catch (err) {
      console.error('Error fetching tickets:', err);
      setError('Failed to load available tickets');
    } finally {
      setLoadingTickets(false);
    }
  };

  // Confirm-on-FINAL gate for line-item edits. Returns true when the invoice
  // is not FINAL (no prompt) or when the user confirms the prompt. Used by
  // add/remove ticket, add/remove markup, and add external service.
  const confirmFinalEdit = (action) => {
    if (invoice?.status !== 'FINAL') return true;
    return window.confirm(
      `This invoice is FINAL. ${action} will change the totals on a finalized invoice. Continue?`
    );
  };

  const handleAddTickets = async () => {
    if (selectedTickets.length === 0) {
      setError('Please select at least one ticket');
      return;
    }
    // Only reachable on a DRAFT invoice; a FINAL one is corrected via Return to Draft or a credit note.

    setAddingTickets(true);
    try {
      const ticketsToAdd = availableTickets.filter(t => selectedTickets.includes(t.id));
      await ApiService.addTicketsToInvoice(invoiceId, ticketsToAdd);
      setTicketDialogOpen(false);
      setSelectedTickets([]);
      setSuccess('Tickets added successfully');
      fetchInvoice();
    } catch (err) {
      console.error('Error adding tickets:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to add tickets');
    } finally {
      setAddingTickets(false);
    }
  };

  const handleRemoveLineItem = async (lineItemId, confirmed = false) => {
    const paid = paidFees(lineItemId);
    if (!confirmed) {
      if (paid.length > 0) { setOutcomes({}); setRemoveTarget(lineItemId); return; }
      if (!window.confirm('Are you sure you want to remove this item from the invoice?')) return;
    }

    try {
      await invoiceMoneyService.removeLine(invoiceId, lineItemId, paid.length ? outcomesBody(paid, outcomes) : undefined);
      setRemoveTarget(null);
      setOutcomes({});
      setSuccess('Item removed successfully');
      fetchInvoice();
      loadMoney();
    } catch (err) {
      console.error('Error removing line item:', err);
      setError(serverMessage(err, 'Failed to remove item'));
    }
  };

  const handleMoveLineItem = async (index, delta) => {
    const items = invoice.lineItems || [];
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    const ids = items.map((li) => li.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    setReordering(true);
    try {
      await ApiService.reorderInvoiceLineItems(invoiceId, ids);
      await fetchInvoice();
    } catch (err) {
      console.error('Error reordering line items:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to reorder line items');
    } finally {
      setReordering(false);
    }
  };

  const openCustomerEditDialog = (item) => {
    setCustomerEditItem(item);
    setCustomerEditValue(item.customer_name || '');
    setDescriptionEditValue(item.description || '');
    setTransactionNumberEditValue(item.transaction_number || '');
    setQuantityEditValue(String(item.quantity || 1));
    // External services expose editable per-unit charges (internal items don't).
    if (item.is_external) {
      const govt = item.govt_charge_input != null ? Number(item.govt_charge_input) : 0;
      const excl = item.service_charge_input != null ? Number(item.service_charge_input) : 0;
      const tax = item.tax_on_service != null ? Number(item.tax_on_service) : 0;
      setGovtChargeEdit(govt ? String(govt) : '');
      setServiceChargeExclEdit(excl ? excl.toFixed(2) : '');
      setTaxEdit(tax ? tax.toFixed(2) : '');
      setServiceChargeInclEdit((excl + tax) ? (excl + tax).toFixed(2) : '');
    } else {
      setGovtChargeEdit('');
      setServiceChargeInclEdit('');
      setServiceChargeExclEdit('');
      setTaxEdit('');
    }
    setCustomerEditOpen(true);
  };

  const handleSaveCustomerName = async () => {
    if (!customerEditItem) return;
    setSavingCustomerName(true);
    try {
      const patch = {
        customer_name: customerEditValue.trim(),
        description: descriptionEditValue.trim() || null,
      };
      if (customerEditItem.is_external) {
        patch.transaction_number = transactionNumberEditValue.trim() || null;
      }
      // Quantity + charges are only editable for direct external services (work-order lines: markup only).
      if (customerEditItem.is_external && !customerEditItem.work_order_line_item_id) {
        const qty = parseInt(quantityEditValue, 10);
        if (!Number.isInteger(qty) || qty < 1) {
          setError('Quantity must be a whole number of at least 1');
          setSavingCustomerName(false);
          return;
        }
        patch.quantity = qty;
        patch.govt_charge = parseFloat(govtChargeEdit) || 0;
        patch.service_charge = parseFloat(serviceChargeExclEdit) || 0; // excl tax, per unit
        patch.tax = parseFloat(taxEdit) || 0;
      }
      await ApiService.updateLineItem(invoiceId, customerEditItem.id, patch);
      setSuccess('Line item updated');
      setCustomerEditOpen(false);
      setCustomerEditItem(null);
      setCustomerEditValue('');
      setDescriptionEditValue('');
      setTransactionNumberEditValue('');
      setQuantityEditValue('1');
      setGovtChargeEdit('');
      setServiceChargeInclEdit('');
      setServiceChargeExclEdit('');
      setTaxEdit('');
      fetchInvoice();
    } catch (err) {
      console.error('Error updating line item:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to update line item');
    } finally {
      setSavingCustomerName(false);
    }
  };

  const handleExternalServiceSuccess = (result) => {
    setSuccess(`External service added: ${result.externalRef}`);
    fetchInvoice();
  };

  const handleFinalize = async () => {
    if (!window.confirm('Are you sure you want to finalize this invoice? This action cannot be undone.')) {
      return;
    }

    setFinalizing(true);
    try {
      await ApiService.finalizeInvoice(invoiceId);
      setSuccess('Invoice finalized successfully');
      fetchInvoice();
    } catch (err) {
      console.error('Error finalizing invoice:', err);
      setError(serverMessage(err, 'Failed to finalize invoice'));
    } finally {
      setFinalizing(false);
    }
  };

  const handleVoid = async () => {
    if (!voidReason.trim()) {
      setError('Please provide a reason for voiding');
      return;
    }

    setVoiding(true);
    try {
      const paid = paidFees();
      await invoiceMoneyService.voidInvoice(invoiceId, voidReason, paid.length ? outcomesBody(paid, outcomes) : undefined);
      setVoidDialogOpen(false);
      setVoidReason('');
      setOutcomes({});
      setSuccess('Invoice voided successfully');
      fetchInvoice();
      loadMoney();
    } catch (err) {
      console.error('Error voiding invoice:', err);
      setError(serverMessage(err, 'Failed to void invoice'));
      setVoidDialogOpen(false);
    } finally {
      setVoiding(false);
    }
  };

  const handleReturnToDraft = async () => {
    setReturning(true);
    try {
      await invoiceMoneyService.returnToDraft(invoiceId, returnReason.trim());
      setReturnOpen(false);
      setReturnReason('');
      setSuccess('Invoice returned to draft — correct it and finalise again');
      fetchInvoice();
      loadMoney();
    } catch (err) {
      setError(serverMessage(err, 'Failed to return the invoice to draft'));
      setReturnOpen(false);
    } finally {
      setReturning(false);
    }
  };

  const deletingRef = useRef(false);
  const handleDeleteDraft = async () => {
    if (deletingRef.current) return;
    deletingRef.current = true;
    setDeleting(true);
    try {
      const paid = paidFees();
      await invoiceMoneyService.deleteInvoice(invoiceId, paid.length ? outcomesBody(paid, outcomes) : undefined);
      navigate('/invoices', { state: { message: `Invoice ${invoice?.invoice_number || ''} deleted` } });
    } catch (err) {
      // Deleted already (another tab, or the list's delete): nothing left here, go back to the list.
      const gone = await ApiService.getInvoiceById(invoiceId).then(() => false).catch((e) => e?.response?.status === 404);
      if (gone) {
        navigate('/invoices', { state: { message: `Invoice ${invoice?.invoice_number || ''} was already deleted` } });
        return;
      }
      setError(serverMessage(err, 'Failed to delete the invoice'));
      setDeleteOpen(false);
    } finally {
      deletingRef.current = false;
      setDeleting(false);
    }
  };

  const openCreditNote = (prefill = null) => { setCreditPrefill(prefill); setCreditOpen(true); };

  // handleMarkPaid removed — customer payment is now tracked via
  // customer_payment_status, mutated atomically by the receipts flow.
  // No standalone mark-paid endpoint exists on the backend.

  const handleGeneratePdf = async () => {
    setGeneratingPdf(true);
    try {
      const response = await ApiService.getInvoicePdfView(
        invoiceId,
        pdfParams
      );
      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${invoice?.invoice_number || invoiceId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 30_000);
      setSuccess('PDF generated successfully');
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to generate PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Open the PDF in a new browser tab instead of downloading it.
  const handleViewPdf = async () => {
    setViewingPdf(true);
    try {
      const response = await ApiService.getInvoicePdfView(
        invoiceId,
        pdfParams
      );
      const blobUrl = window.URL.createObjectURL(response.data);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
    } catch (err) {
      console.error('Error opening PDF:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to open PDF');
    } finally {
      setViewingPdf(false);
    }
  };

  const handleApplyMarkup = async () => {
    if (!markupAmount || parseFloat(markupAmount) <= 0) {
      setError('Please enter a valid markup amount');
      return;
    }
    if (!confirmFinalEdit('Applying invoice markup')) return;

    setApplyingMarkup(true);
    try {
      await ApiService.applyInvoiceMarkup(invoiceId, {
        markupAmount: parseFloat(markupAmount),
        markupType: 'fixed'
      });
      setMarkupDialogOpen(false);
      setMarkupAmount('');
      setSuccess(`AED ${markupAmount} markup applied successfully`);
      fetchInvoice();
    } catch (err) {
      console.error('Error applying markup:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to apply markup');
    } finally {
      setApplyingMarkup(false);
    }
  };

  const handleRemoveMarkup = async () => {
    const prompt = invoice?.status === 'FINAL'
      ? 'This invoice is FINAL. Removing all markup will change the totals on a finalized invoice. Continue?'
      : 'Are you sure you want to remove all markup from this invoice?';
    if (!window.confirm(prompt)) return;

    setRemovingMarkup(true);
    try {
      await ApiService.removeInvoiceMarkup(invoiceId);
      setSuccess('Markup removed successfully');
      fetchInvoice();
    } catch (err) {
      console.error('Error removing markup:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to remove markup');
    } finally {
      setRemovingMarkup(false);
    }
  };

  // Line item markup handlers
  const openLineItemMarkupDialog = (lineItem) => {
    setSelectedLineItem(lineItem);
    // Pre-fill: detect sign of existing adjustment and open the matching tab.
    // The input always carries the absolute value; the tab encodes direction.
    const existingInclTax = (lineItem.service_charge_markup || 0) + (lineItem.tax_markup || 0);
    if (existingInclTax < 0) {
      setMarkupDialogTab('discount');
      setLineItemMarkupAmount(String(Math.abs(existingInclTax)));
    } else {
      setMarkupDialogTab('markup');
      setLineItemMarkupAmount(existingInclTax > 0 ? String(existingInclTax) : '');
    }
    setLineItemMarkupDialogOpen(true);
  };

  const handleApplyLineItemMarkup = async () => {
    if (!selectedLineItem || !lineItemMarkupAmount || parseFloat(lineItemMarkupAmount) <= 0) {
      setError(`Please enter a valid ${markupDialogTab} amount`);
      return;
    }

    const isDiscount = markupDialogTab === 'discount';
    const isInternal = !selectedLineItem.is_external;

    if (isDiscount && isInternal) {
      // Discounting an internal item: the TC still owes Thaklis the full
      // internal cost, so the discount is a direct loss against the TC's
      // own markup column. Make the user acknowledge that explicitly.
      const ok = window.confirm(
        'This is an internal line item. Applying a discount means the typing center absorbs the loss (Thaklis is still paid in full). Continue?'
      );
      if (!ok) return;
    }

    // The input is always positive; the tab encodes direction. Discount → negative.
    const sign = isDiscount ? -1 : 1;
    const inputInclTax = parseFloat(lineItemMarkupAmount);
    const signedInclTax = sign * inputInclTax;
    const signedExclTax = Number((signedInclTax / 1.05).toFixed(2));
    const signedTaxOnMarkup = Number((signedInclTax - signedInclTax / 1.05).toFixed(2));

    setApplyingLineItemMarkup(true);
    try {
      await ApiService.applyLineItemMarkup(selectedLineItem.id, {
        markupAmount: signedExclTax,
        tax: signedTaxOnMarkup
      });
      setLineItemMarkupDialogOpen(false);
      setSelectedLineItem(null);
      setLineItemMarkupAmount('');
      setMarkupDialogTab('markup');
      setSuccess(isDiscount ? 'Discount applied to ticket successfully' : 'Markup applied to ticket successfully');
      fetchInvoice();
    } catch (err) {
      console.error('Error applying line item markup:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to apply');
    } finally {
      setApplyingLineItemMarkup(false);
    }
  };

  const handleRemoveLineItemMarkup = async (lineItemId) => {
    if (!window.confirm('Remove markup from this ticket?')) return;

    try {
      await ApiService.removeLineItemMarkup(lineItemId);
      setSuccess('Markup removed from ticket');
      fetchInvoice();
    } catch (err) {
      console.error('Error removing line item markup:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to remove markup');
    }
  };

  // Email handlers
  const openEmailDialog = async () => {
    setEmailDialogOpen(true);
    setEmailRecipients([]);
    setEmailCc('');
    setLoadingEmailHistory(true);

    try {
      // Fetch organization config to get default emails
      if (invoice?.typing_center_id && invoice?.organization_id) {
        const configResponse = await ApiService.getOrganizationConfigByTcAndOrg(
          invoice.typing_center_id,
          invoice.organization_id
        );
        if (configResponse.data?.data?.default_emails) {
          const defaultEmails = configResponse.data.data.default_emails;
          if (Array.isArray(defaultEmails) && defaultEmails.length > 0) {
            setEmailRecipients(defaultEmails);
            console.log('[InvoiceDetail] Pre-populated emails from org config:', defaultEmails);
          }
        }
      }

      // Fetch email history
      const response = await ApiService.get(`/api/invoices/${invoiceId}/email-history`);
      if (response.data?.data) {
        setEmailHistory(response.data.data);
      }
    } catch (err) {
      console.error('Error in email dialog setup:', err);
    } finally {
      setLoadingEmailHistory(false);
    }
  };

  const handleAddRecipient = () => {
    const email = prompt('Enter email address:');
    if (email && !emailRecipients.includes(email)) {
      setEmailRecipients(prev => [...prev, email]);
    }
  };

  const handleRemoveRecipient = (email) => {
    setEmailRecipients(prev => prev.filter(e => e !== email));
  };

  const handleSendEmail = async () => {
    if (emailRecipients.length === 0) {
      setError('Please add at least one recipient email address');
      return;
    }

    setSendingEmail(true);
    try {
      const ccList = emailCc ? emailCc.split(',').map(e => e.trim()).filter(e => e) : [];
      await ApiService.post(`/api/invoices/${invoiceId}/send-email`, {
        recipients: emailRecipients,
        ccEmails: ccList,
        attachPdf: true
      });
      setSuccess('Invoice sent successfully via email');
      setEmailDialogOpen(false);

      // Refresh email history
      const response = await ApiService.get(`/api/invoices/${invoiceId}/email-history`);
      if (response.data?.data) {
        setEmailHistory(response.data.data);
      }
    } catch (err) {
      console.error('Error sending email:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to send email. Please check email configuration.');
    } finally {
      setSendingEmail(false);
    }
  };

  const openUpdateDialog = () => {
    const currentDate = invoice?.invoice_date;
    setUpdateInvoiceDateInput(currentDate ? dayjs(currentDate) : dayjs());
    setUpdateSubjectInput(invoice?.subject || '');

    setSelectedInternalOrgId('');
    setSelectedInternalOrg(null);
    setInternalOrgSearch('');
    // The list loads via the debounced search effect once the dialog is open.
    setUpdateDialogOpen(true);
  };

  const closeUpdateDialog = () => {
    setUpdateDialogOpen(false);
    setUpdateInvoiceDateInput(null);
    setUpdateSubjectInput('');
    setSelectedInternalOrgId('');
    setSelectedInternalOrg(null);
    setInternalOrgSearch('');
  };

  const handleSaveUpdate = async () => {
    if (!updateInvoiceDateInput || !updateInvoiceDateInput.isValid()) {
      setError('Please select a valid invoice date');
      return;
    }

    const payload = {
      subject: updateSubjectInput,
    };
    if (invoice?.status !== 'FINAL') payload.invoiceDate = updateInvoiceDateInput.format('YYYY-MM-DD');

    // Only send internalOrganizationId when it actually changed.
    // Allowed in DRAFT and FINAL; backend rejects elsewhere.
    const currentInternalOrgId = invoice?.internal_organization_id || invoice?.internalOrganizationId || '';
    if (
      isEditable &&
      invoice?.status !== 'FINAL' &&
      ['TYPING_CENTER', 'B2B'].includes(typingCenterOrgType) &&
      selectedInternalOrgId &&
      selectedInternalOrgId !== currentInternalOrgId
    ) {
      payload.internalOrganizationId = selectedInternalOrgId;
    }

    setSavingUpdate(true);
    try {
      await ApiService.updateInvoice(invoiceId, payload);
      closeUpdateDialog();
      setSuccess('Invoice updated successfully');
      fetchInvoice();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to update invoice');
    } finally {
      setSavingUpdate(false);
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      DRAFT: { color: 'warning', label: 'Draft' },
      FINAL: { color: 'info', label: 'Final' },
      PAID: { color: 'success', label: 'Paid' },
      PARTIALLY_PAID: { color: 'warning', label: 'Partially Paid' },
      VOID: { color: 'error', label: 'Void' }
    };
    const config = statusConfig[status] || { color: 'info', label: status, variant: 'outlined' };
    return <Chip label={config.label} color={config.color} variant={config.variant} />;
  };


  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED'
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!invoice) {
    return (
      <Box>
        <Alert severity="error">Invoice not found</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/invoices')} sx={{ mt: 2 }}>
          Back to Invoices
        </Button>
      </Box>
    );
  }

  const isDraft = invoice.status === 'DRAFT';
  const isFinal = invoice.status === 'FINAL';
  const isPaid = invoice.status === 'PAID';
  const isVoid = invoice.status === 'VOID';
  // Write actions follow the permission matrix (TC_ADMIN/platform resolve all-true).
  const canWriteInv = can(currentUser, 'invoice', 'write');
  const canWriteFin = can(currentUser, 'finance', 'write');

  // Edits are allowed in DRAFT and FINAL. Once the TC → Thaklis topup is
  // PENDING or PAID on this invoice, internal (ticket-backed) changes are
  // locked because they would desync the amount already sent to Thaklis.
  // External line items and markups remain freely editable regardless.
  const isThaklisLocked = ['PAID', 'PENDING'].includes(invoice.thaklis_payment_status);
  const isEditable = (isDraft || isFinal) && canWriteInv;
  // Line items edit only on DRAFT; a FINAL invoice goes back via Return to Draft.
  const lineItemsUnlocked = isDraft && canWriteInv;
  const isCredited = Number(invoice.credited_amount || 0) > 0;
  const feePaymentOf = (lineId) => feePayments.find((fp) => fp.lineItemId === lineId && fp.status === 'RECORDED');
  const feeChip = (item) => {
    const fp = feePaymentOf(item.id);
    // a customer-card payment zeroes the billed fee - the line still shows it was paid, and how
    if (!fp && (!isDirectFeeLine(item) || invoiceConfig?.skip_govt_fee_payment === true)) return null;
    const ev = EVIDENCE[item.disbursement_review_status];
    // evidence is optional: only started evidence (authorisation, receipt no. or file) that is not accepted shows
    const started = Boolean(item.disbursement_auth_type || item.disbursement_receipt_ref || item.disbursement_has_receipt);
    const needsEvidence = fp && fp.source !== 'CUSTOMER_CARD' && started && item.disbursement_review_status !== 'ACCEPTED';
    const label = !fp ? 'Gov fee not paid' : needsEvidence ? (ev ? ev.label : 'Evidence needed') : 'Gov fee paid';
    const tone = !fp ? 'warning' : needsEvidence ? (ev?.tone || 'error') : 'success';
    return (
      <Box component="span" sx={{ cursor: 'pointer' }} onClick={() => setFeeLineId(item.id)}>
        <StatusChip tone={tone} label={label} />
      </Box>
    );
  };
  const woChip = (item) => item.work_order_line_item_id && (
    <Tooltip title="From a work order — amounts come from the work order; only markup changes here">
      <Chip label="Work order" size="small" sx={{ height: 16, fontSize: '0.65rem' }} />
    </Tooltip>
  );
  // Adding Thaklis tickets is hidden when the TC has Thaklis features disabled.
  const thaklisDisabled = thaklisServicesOff;
  const canAddTicket = lineItemsUnlocked && !isThaklisLocked && !thaklisDisabled;
  const canRemoveInternal = lineItemsUnlocked && !isThaklisLocked;
  // KAN-242 (D6/D12): add completed work order lines of this invoice's customer to the draft
  const openWorkOrderPicker = () => {
    const orgId = invoice.internal_organization_id || invoice.internalOrganizationId;
    const q = new URLSearchParams({
      tc: invoice.typing_center_id || invoice.typingCenterId || '',
      orgName: invoice.internal_organization_name || invoice.internalOrganizationName || (orgId ? 'Client' : 'Walk-in'),
      invoice: invoiceId,
      invoiceNumber: invoice.invoice_number || invoice.invoiceNumber || '',
    });
    if (orgId) q.set('org', orgId); else q.set('walkIn', '1');
    navigate(`/invoices/new/items?${q.toString()}`);
  };

  // Line-item table config (mirrors the invoice PDF). Defaults: show customer,
  // "Service Chg" label. show_customer_column is only hidden when explicitly false.
  const showCustomerCol = invoiceConfig?.show_customer_column !== false;
  const showTicketNumber = invoiceConfig?.show_ticket_number !== false;
  const svcChargeLabel = invoiceConfig?.svc_charge_label || 'Service Chg';
  // "Fees" when any line carries a fine or add-ons (summed with the government fee), else "Govt Chg"
  const feesLabel = hasFeeExtras(invoice?.lineItems) ? 'Fees' : 'Govt Chg';
  // Detail page only (never on the PDF): the fine folded into Fees
  const fineNote = (item) => (Number(item.fine || 0) > 0 ? (
    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>includes {formatCurrency(item.fine)} fine</Typography>
  ) : null);

  // Toggle expand for mobile card view
  const toggleExpand = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  return (
    <Box sx={{ pb: isMobile ? 2 : 0 }}>
      {/* Header - Mobile Responsive */}
      <Box
        display="flex"
        flexDirection={isSmallMobile ? 'column' : 'row'}
        justifyContent="space-between"
        alignItems={isSmallMobile ? 'flex-start' : 'center'}
        mb={3}
        gap={isSmallMobile ? 2 : 0}
      >
        <Box display="flex" alignItems="center">
          <IconButton onClick={() => navigate('/invoices')} sx={{ mr: 1 }}>
            <BackIcon />
          </IconButton>
          <Box>
            <Typography variant={isSmallMobile ? 'h6' : 'h5'} fontWeight={600}>
              {invoice.invoice_number}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: isSmallMobile ? '0.8rem' : '0.875rem' }}>
              {invoice.internal_organization_name || '—'}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" alignItems="center" gap={1} sx={{ ml: isSmallMobile ? 5 : 0 }}>
          {getStatusChip(invoice.status)}
          {Number(invoice.revision) > 0 && <Chip size="small" variant="outlined" label={`Revision ${invoice.revision}`} />}
          <IconButton onClick={() => { fetchInvoice(); loadMoney(); }} size={isSmallMobile ? 'small' : 'medium'}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {partnerPrompts.map((note) => {
        const rows = note.lines || [];
        const total = rows.reduce((t, r) => t + Number(r.amount || 0), 0);
        return (
          <Alert key={note.partnerCreditNoteId} severity="warning" sx={{ mb: 2 }}
            action={isFinal && canWriteInv && (
              <Button color="inherit" size="small" onClick={() => openCreditNote({
                reason: `Partner credit note ${note.partnerCreditNoteNumber}: ${note.reason || ''}`.trim(),
                rows: rows.map((r) => ({ lineId: r.invoiceLineItemId, component: r.component, amount: r.amount })),
              })}>
                Issue credit note
              </Button>
            )}>
            {note.partnerName} credited {formatCurrency(total)} ({note.partnerCreditNoteNumber}) on items you billed to {rows[0]?.customerName}
            {' — '}{rows.map((r) => `${r.serviceName}: ${COMPONENT_LABEL[r.component] || r.component}`).join(', ')}. Issue a credit note to your customer?
          </Alert>
        );
      })}

      <Grid container spacing={isMobile ? 2 : 3}>
        {/* Invoice Info */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: isMobile ? 2 : 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={isMobile ? 2 : 3}>
              <Typography variant="h6" fontWeight={600} sx={{ fontSize: isMobile ? '1rem' : '1.1rem' }}>
                Invoice Details
              </Typography>
              {isEditable && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={openUpdateDialog}
                  sx={{ textTransform: 'none' }}
                >
                  Update
                </Button>
              )}
            </Box>

            <Box display="flex" gap={2} mb={1.5}>
              <Box flex={1}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Invoice Number</Typography>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.9rem' }}>
                  {invoice.invoice_number}
                </Typography>
              </Box>
              <Box flex={1}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Customer Organization</Typography>
                <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>{invoice.internal_organization_name || '—'}</Typography>
              </Box>
            </Box>

            <Box mb={1.5}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Invoice Date</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>{formatDate(invoice.invoice_date)}</Typography>
            </Box>

            {invoice.due_date && (
              <Box mb={1.5}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Due Date</Typography>
                <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>{formatDate(invoice.due_date)}</Typography>
              </Box>
            )}

            {invoice.subject && (
              <Box mb={1.5}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Subject</Typography>
                <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>{invoice.subject}</Typography>
              </Box>
            )}

            <Box mb={1.5}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Created By</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>{invoice.created_by_name || invoice.created_by || 'System'}</Typography>
            </Box>

            {/* Source Information - Work Order or Quotation */}
            {(invoice.work_order_id || invoice.workOrderId || invoice.quotation_id || invoice.quotationId) && (
              <Box mb={1.5} sx={{ bgcolor: 'action.selected', p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                  Source
                </Typography>
                {(invoice.work_order_id || invoice.workOrderId) && (
                  <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.5}>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>Work Order</Typography>
                    <Chip
                      label={invoice.work_order_number || invoice.workOrderNumber}
                      size="small"
                      color="info"
                      onClick={() => navigate(`/work-orders/${invoice.work_order_id || invoice.workOrderId}`)}
                      sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
                    />
                  </Box>
                )}
                {(invoice.quotation_id || invoice.quotationId) && (
                  <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.5}>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>Quotation</Typography>
                    <Chip
                      label={invoice.quotation_number || invoice.quotationNumber}
                      size="small"
                      color="secondary"
                      onClick={() => navigate(`/quotations/${invoice.quotation_id || invoice.quotationId}`)}
                      sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
                    />
                  </Box>
                )}
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Financial Summary */}
            <Typography variant="body2" fontWeight={600} mb={1} sx={{ fontSize: '0.9rem' }}>
              Summary
            </Typography>

            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>Items</Typography>
              <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.85rem' }}>{invoice.item_count}</Typography>
            </Box>

            {/* Subtotal and tax with tax details on, or with Subtotal & Tax on, as the PDF (drawTotals) */}
            {showSubtotalAndTax && (
              <>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>Subtotal</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{formatCurrency(invoice.subtotal)}</Typography>
                </Box>

                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>Tax</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{formatCurrency(invoice.tax_total)}</Typography>
                </Box>
              </>
            )}

            <Divider sx={{ my: 1 }} />

            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.9rem' }}>Grand Total</Typography>
              <Typography variant="body2" fontWeight={600} color="primary" sx={{ fontSize: '0.9rem' }}>
                {formatCurrency(invoice.grand_total)}
              </Typography>
            </Box>

            {/* Show Base, Markup, Total when markup is applied */}
            {(invoice.total_commission > 0 || invoice.total_markup > 0) ? (
              <>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(46,125,50,0.1)' : '#f0fdf4', p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" color="success.dark" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
                    PRICING BREAKDOWN
                  </Typography>
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>Base (Cost to Thaklis)</Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{formatCurrency(invoice.original_grand_total)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>+ Your Markup</Typography>
                    <Typography variant="body2" color="success.main" fontWeight={600} sx={{ fontSize: '0.85rem' }}>
                      +{formatCurrency(invoice.total_commission || invoice.total_markup)}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 0.5 }} />
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.9rem' }}>Invoice Total</Typography>
                    <Typography variant="body2" fontWeight={600} color="primary" sx={{ fontSize: '0.9rem' }}>
                      {formatCurrency(invoice.grand_total)}
                    </Typography>
                  </Box>
                </Box>
              </>
            ) : null}

            <Divider sx={{ my: 1 }} />

            {/* Synced to Thaklis status */}
            {invoice.synced_to_thaklis && (
              <Box display="flex" justifyContent="space-between" mb={0.5}>
                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>Synced to Thaklis</Typography>
                <Typography variant="body2" color="info.main" sx={{ fontSize: '0.85rem' }}>
                  {formatCurrency(invoice.synced_amount || invoice.original_grand_total)}
                </Typography>
              </Box>
            )}

            {/* TC → Thaklis payment state.
                internal_total = ticket cost payable to Thaklis (no markup).
                thaklis_payment_status = UNPAID / PENDING / PAID / REJECTED. */}
            {!thaklisDisabled && invoice.thaklis_payment_status && (
              <Box sx={{ my: 1, p: 1.25, borderRadius: 1, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontWeight: 600, color: 'secondary.main' }}>
                  THAKLIS PAYMENT
                </Typography>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>Payable to Thaklis</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                    {formatCurrency(invoice.internal_total || 0)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>Payment Status</Typography>
                  <Chip
                    size="small"
                    label={invoice.thaklis_payment_status}
                    color={
                      invoice.thaklis_payment_status === 'PAID' ? 'success' :
                      invoice.thaklis_payment_status === 'PENDING' ? 'warning' :
                      invoice.thaklis_payment_status === 'REJECTED' ? 'error' :
                      'default'
                    }
                  />
                </Box>
              </Box>
            )}

            {/* Customer-side payment status / paid / due intentionally
                lives in the Customer Payment History section below to
                avoid duplication. */}
          </Paper>

          {/* Profit Summary */}
          {invoice.internal_total > 0 && (
            <Paper sx={{ p: 3, mt: 3 }}>
              <Typography variant="body2" fontWeight={600} mb={1.5} sx={{ fontSize: '0.9rem' }}>
                Profit Summary
              </Typography>

              <Box display="flex" justifyContent="space-between" mb={0.5}>
                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>Revenue (Grand Total)</Typography>
                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{formatCurrency(invoice.grand_total)}</Typography>
              </Box>

              <Box display="flex" justifyContent="space-between" mb={0.5}>
                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>Cost (Internal Total)</Typography>
                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{formatCurrency(invoice.internal_total)}</Typography>
              </Box>

              <Box display="flex" justifyContent="space-between" mb={0.5}>
                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>Commission</Typography>
                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{formatCurrency(invoice.total_commission || 0)}</Typography>
              </Box>

              <Divider sx={{ my: 1 }} />

              {(() => {
                const profit = (invoice.grand_total || 0) - (invoice.internal_total || 0);
                const margin = invoice.grand_total ? (profit / invoice.grand_total) * 100 : 0;
                return (
                  <>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.9rem' }}>Net Profit</Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.9rem', color: profit >= 0 ? 'success.main' : 'error.main' }}>
                        {formatCurrency(profit)}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>Margin</Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.85rem', color: profit >= 0 ? 'success.main' : 'error.main' }}>
                        {margin.toFixed(1)}%
                      </Typography>
                    </Box>
                  </>
                );
              })()}
            </Paper>
          )}

          {/* Actions */}
          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" fontWeight={600} mb={2} sx={{ fontSize: '1.1rem' }}>
              Actions
            </Typography>

            <Box display="flex" flexDirection="column" gap={1}>
              {lineItemsUnlocked && (
                <Button variant="contained" startIcon={<AddIcon />} onClick={openWorkOrderPicker} fullWidth>
                  Add from work orders
                </Button>
              )}
              {canAddTicket && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    fetchAvailableTickets();
                    setTicketDialogOpen(true);
                  }}
                  fullWidth
                >
                  Add Tickets
                </Button>
              )}

              {lineItemsUnlocked && (
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setExternalServiceDialogOpen(true)}
                  fullWidth
                  sx={{
                    borderColor: 'primary.main',
                    color: 'secondary.main',
                    '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' }
                  }}
                >
                  Add External Service
                </Button>
              )}

              {isDraft && canWriteInv && (
                <Button
                  variant="contained"
                  startIcon={finalizing ? <CircularProgress size={20} /> : <FinalizeIcon />}
                  onClick={handleFinalize}
                  disabled={finalizing || invoice.item_count === 0}
                  fullWidth
                  sx={{
                    bgcolor: 'success.dark',
                    color: '#fff',
                    '&:hover': { bgcolor: 'success.main' },
                    '&:disabled': { bgcolor: 'action.disabledBackground', color: 'text.disabled' }
                  }}
                >
                  {finalizing ? 'Finalizing...' : 'Finalize Invoice'}
                </Button>
              )}

              {isDraft && can(currentUser, 'invoice', 'delete') && (
                <Button variant="outlined" color="error" startIcon={<DeleteIcon />} fullWidth
                  onClick={() => { setOutcomes({}); setDeleteOpen(true); }}>
                  Delete Draft
                </Button>
              )}

              {isFinal && canWriteInv && (
                <>
                  {/* Customer payment is now driven by the Receipts flow
                      (`/portal/payments` for the customer; Settlements
                      wizard for the TC). The legacy "Customer Paid -
                      Mark Complete" button is removed; customer payment
                      status flows through the receipts ledger and is
                      reflected in the customer_payment_status chip. */}
                  <Button
                    variant="contained"
                    startIcon={<CreditNoteIcon />}
                    onClick={() => openCreditNote()}
                    fullWidth
                  >
                    Issue Credit Note
                  </Button>
                  {!isCredited && (
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<ReturnDraftIcon />}
                      onClick={() => { setReturnReason(''); setReturnOpen(true); }}
                      fullWidth
                    >
                      Return to Draft
                    </Button>
                  )}
                  {!isCredited && (
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<VoidIcon />}
                      onClick={() => { setOutcomes({}); setVoidDialogOpen(true); }}
                      fullWidth
                      sx={{
                        borderColor: 'error.main',
                        color: 'error.main',
                        '&:hover': { borderColor: 'error.main', bgcolor: 'action.hover' }
                      }}
                    >
                      Void Invoice
                    </Button>
                  )}
                </>
              )}

              {(isFinal || isPaid) && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={viewingPdf ? <CircularProgress size={20} /> : <ViewPdfIcon />}
                    onClick={handleViewPdf}
                    disabled={viewingPdf}
                    fullWidth
                  >
                    {viewingPdf ? 'Opening...' : 'View PDF'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={generatingPdf ? <CircularProgress size={20} /> : <PdfIcon />}
                    onClick={handleGeneratePdf}
                    disabled={generatingPdf}
                    fullWidth
                  >
                    {generatingPdf ? 'Generating...' : 'Download PDF'}
                  </Button>
                  {canWriteInv && (
                  <Button
                    variant="outlined"
                    startIcon={<EmailIcon />}
                    onClick={openEmailDialog}
                    fullWidth
                    sx={{
                      borderColor: 'info.main',
                      color: 'info.main',
                      '&:hover': { borderColor: 'info.main', bgcolor: 'action.hover' }
                    }}
                  >
                    Send via Email
                  </Button>
                  )}
                </>
              )}

              {isVoid && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  This invoice has been voided.
                  {invoice.void_reason && (
                    <Typography variant="body2">Reason: {invoice.void_reason}</Typography>
                  )}
                </Alert>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Line Items */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: isMobile ? 2 : 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
              <Typography variant="h6" fontWeight={600} sx={{ fontSize: isMobile ? '1rem' : '1.1rem' }}>
                Line Items ({invoice.lineItems?.length || 0})
              </Typography>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" justifyContent="flex-end">
                <FormControlLabel
                  control={
                    <Switch
                      checked={includeTaxDetails}
                      onChange={(e) => updatePdfPrefs({ includeTaxDetails: e.target.checked })}
                      size="small"
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: 'secondary.main',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          bgcolor: 'primary.main',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                      Tax Details
                    </Typography>
                  }
                  labelPlacement="start"
                  sx={{ mr: 1 }}
                />
                {!includeTaxDetails && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showSubtotalTax}
                        onChange={(e) => updatePdfPrefs({ showSubtotalTax: e.target.checked })}
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: 'secondary.main',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            bgcolor: 'primary.main',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                        Subtotal &amp; Tax
                      </Typography>
                    }
                    labelPlacement="start"
                    sx={{ mr: 1 }}
                  />
                )}

                {canAddTicket && (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      fetchAvailableTickets();
                      setTicketDialogOpen(true);
                    }}
                  >
                    Add Tickets
                  </Button>
                )}
                {lineItemsUnlocked && (
                  <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={openWorkOrderPicker}>
                    Add from work orders
                  </Button>
                )}
              </Box>
            </Box>

            {isFinal && (
              <Alert severity="info" sx={{ mb: 2 }}>
                This invoice is FINAL — its amounts are locked.{' '}
                {isCredited ? 'Issue another credit note to correct it.' : 'Return it to draft to correct it, or issue a credit note.'}
              </Alert>
            )}

            {/* Mobile Card View */}
            {isMobile ? (
              <Stack spacing={2}>
                {(!invoice.lineItems || invoice.lineItems.length === 0) ? (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                      No line items. Click "Add Tickets" to add tickets.
                    </Typography>
                  </Box>
                ) : (
                  invoice.lineItems.map((item, idx) => {
                    const adjAmount = item.service_charge_markup ?? 0;
                    const hasAdjustment = adjAmount !== 0;
                    const isDiscount = adjAmount < 0;
                    // Kept for the few places that still gate on "has positive markup"
                    // visual emphasis. Equivalent to the old name when isDiscount=false.
                    const hasMarkup = hasAdjustment;
                    const adjColor = isDiscount ? 'error.main' : 'success.main';
                    const adjLightColor = isDiscount ? 'error.light' : 'success.light';
                    const adjBg = isDiscount
                      ? ((theme) => theme.palette.mode === 'dark' ? 'rgba(211,47,47,0.10)' : '#fef2f2')
                      : ((theme) => theme.palette.mode === 'dark' ? 'rgba(46,125,50,0.10)' : '#f0fdf4');
                    const adjSign = adjAmount > 0 ? '+' : adjAmount < 0 ? '−' : '';
                    const adjLabel = isDiscount ? 'Discount' : 'Markup';
                    const originalServiceCharge = item.original_service_charge ?? item.service_charge;
                    const isExpanded = expandedItems[item.id];
                    return (
                      <Card
                        key={item.id}
                        variant="outlined"
                        sx={{
                          bgcolor: hasAdjustment ? adjBg : 'background.paper',
                          border: '1px solid',
                          borderColor: hasAdjustment ? adjLightColor : 'divider'
                        }}
                      >
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          {/* Header Row */}
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                            <Box flex={1}>
                              <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                                {showTicketNumber && (
                                  <Typography fontWeight={600} sx={{ fontSize: '0.9rem' }}>
                                    {item.ticket_number}
                                  </Typography>
                                )}
                                {item.is_external && (
                                  <Chip
                                    label="External"
                                    size="small"
                                    sx={{
                                      height: 16,
                                      fontSize: '0.6rem',
                                      bgcolor: 'action.selected',
                                      color: 'secondary.main'
                                    }}
                                  />
                                )}
                                {woChip(item)}
                                {feeChip(item)}
                                {lineItemsUnlocked && (
                                  <Tooltip title="Edit line item">
                                    <IconButton size="small" onClick={() => openCustomerEditDialog(item)} sx={{ p: 0.25 }}>
                                      <EditIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                              <Typography color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                {item.service_name}
                              </Typography>
                              {item.description && (
                                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                  {item.description}
                                </Typography>
                              )}
                              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                Qty: {item.quantity || 1}
                              </Typography>
                              {transactionNumbersOf(item).length > 0 && (
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
                                  {transactionNumbersOf(item).map((number) => (
                                    <Chip key={number} label={number} size="small" variant="outlined"
                                      sx={{ fontSize: '0.65rem', height: 18 }} />
                                  ))}
                                </Box>
                              )}
                            </Box>
                            <Box textAlign="right">
                              <Typography fontWeight={700} color="primary" sx={{ fontSize: '1rem' }}>
                                {formatCurrency(rowCharges(item).total)}
                              </Typography>
                              {hasMarkup && (
                                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                  Base: {formatCurrency(item.original_line_total)}
                                </Typography>
                              )}
                            </Box>
                          </Box>

                          {/* Customer */}
                          {showCustomerCol && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                              <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                {item.customer_name || '-'}
                              </Typography>
                            </Box>
                          )}

                          {/* Expandable Details */}
                          <Box
                            onClick={() => toggleExpand(item.id)}
                            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', color: 'secondary.main', mb: isExpanded ? 1 : 0 }}
                          >
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                              {isExpanded ? 'Hide Details' : 'Show Details'}
                            </Typography>
                            {isExpanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                          </Box>

                          <Collapse in={isExpanded}>
                            <Divider sx={{ my: 1 }} />
                            <Grid container spacing={1}>
                              <Grid item xs={6}>
                                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{adjLabel}</Typography>
                                <Typography sx={{ fontSize: '0.8rem', color: hasAdjustment ? adjColor : 'text.secondary', fontWeight: hasAdjustment ? 600 : 400 }}>
                                  {hasAdjustment ? `${adjSign}${formatCurrency(Math.abs(item.service_charge_markup))}` : '-'}
                                </Typography>
                              </Grid>
                              {includeTaxDetails ? (
                                <>
                                  <Grid item xs={6}>
                                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{feesLabel}</Typography>
                                    <Typography sx={{ fontSize: '0.8rem' }}>{formatCurrency(lineFees(item))}</Typography>
                                    {fineNote(item)}
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{svcChargeLabel}</Typography>
                                    <Typography sx={{ fontSize: '0.8rem' }}>{formatCurrency(originalServiceCharge)}</Typography>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Tax</Typography>
                                    <Typography sx={{ fontSize: '0.8rem' }}>
                                      {formatCurrency(Number(item.tax_value || 0) + Number(item.addon_vat || 0))}
                                      {hasAdjustment && (item.tax_markup ?? 0) !== 0 && (
                                        <Typography component="span" sx={{ color: adjColor, fontSize: '0.7rem' }}>
                                          {' '}({item.tax_markup > 0 ? '+' : '−'}{formatCurrency(Math.abs(item.tax_markup))})
                                        </Typography>
                                      )}
                                    </Typography>
                                  </Grid>
                                </>
                              ) : (
                                <Grid item xs={6}>
                                  <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Fees/Rate</Typography>
                                  <Typography sx={{ fontSize: '0.8rem' }}>{formatCurrency(rowCharges(item).total)}</Typography>
                                </Grid>
                              )}
                            </Grid>

                            {/* Actions — DRAFT only; internal deletion also needs !isThaklisLocked. */}
                            {lineItemsUnlocked && (
                              <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1} mt={2}>
                                <IconButton size="small" disabled={reordering || idx === 0} onClick={() => handleMoveLineItem(idx, -1)}>
                                  <ArrowUpIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" disabled={reordering || idx === invoice.lineItems.length - 1} onClick={() => handleMoveLineItem(idx, 1)}>
                                  <ArrowDownIcon fontSize="small" />
                                </IconButton>
                                {hasAdjustment ? (
                                  <>
                                    <Button size="small" variant="outlined" color={isDiscount ? 'error' : 'success'} onClick={() => openLineItemMarkupDialog(item)}>
                                      Edit {adjLabel}
                                    </Button>
                                    <Button size="small" variant="outlined" color="error" onClick={() => handleRemoveLineItemMarkup(item.id)}>
                                      Remove
                                    </Button>
                                  </>
                                ) : (
                                  <Button size="small" variant="outlined" color="success" onClick={() => openLineItemMarkupDialog(item)}>
                                    Add Markup / Discount
                                  </Button>
                                )}
                                {(((item.is_external || item.work_order_line_item_id) && lineItemsUnlocked) || (!item.is_external && canRemoveInternal)) && (
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleRemoveLineItem(item.id)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Box>
                            )}
                          </Collapse>
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </Stack>
            ) : (
              /* Desktop Table View */
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { bgcolor: 'action.hover', color: 'text.primary', fontWeight: 600, fontSize: '0.85rem' } }}>
                      {showTicketNumber && <TableCell sx={{ whiteSpace: 'nowrap' }}>Ticket #</TableCell>}
                      <TableCell>Service</TableCell>
                      <TableCell align="center">Qty</TableCell>
                      {showCustomerCol && <TableCell>Customer</TableCell>}
                      <TableCell align="right">+ Markup / − Discount</TableCell>
                      {includeTaxDetails && <TableCell align="right">{feesLabel}</TableCell>}
                      <TableCell align="right">{includeTaxDetails ? svcChargeLabel : 'Fees/Rate'}</TableCell>
                      {includeTaxDetails && <TableCell align="right">Tax</TableCell>}
                      <TableCell align="right">Total</TableCell>
                    {lineItemsUnlocked && <TableCell align="center">Action</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(!invoice.lineItems || invoice.lineItems.length === 0) ? (
                    <TableRow>
                      <TableCell
                        colSpan={
                          5
                          + (showTicketNumber ? 1 : 0)
                          + (showCustomerCol ? 1 : 0)
                          + (includeTaxDetails ? 2 : 0)
                          + (lineItemsUnlocked ? 1 : 0)
                        }
                        align="center"
                        sx={{ py: 4 }}
                      >
                        <Typography color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                          No line items. Click "Add Tickets" to add tickets to this invoice.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    invoice.lineItems.map((item, idx) => {
                      const adjAmount = item.service_charge_markup ?? 0;
                      const hasAdjustment = adjAmount !== 0;
                      const isDiscount = adjAmount < 0;
                      const adjColor = isDiscount ? 'error.main' : 'success.main';
                      const adjSign = adjAmount > 0 ? '+' : adjAmount < 0 ? '−' : '';
                      const adjLabel = isDiscount ? 'discount' : 'markup';
                      const rowBg = hasAdjustment
                        ? (isDiscount
                            ? ((theme) => theme.palette.mode === 'dark' ? 'rgba(211,47,47,0.10)' : '#fef2f2')
                            : ((theme) => theme.palette.mode === 'dark' ? 'rgba(46,125,50,0.10)' : '#f0fdf4'))
                        : undefined;
                      const originalServiceCharge = item.original_service_charge ?? item.service_charge;
                      return (
                      <TableRow key={item.id} hover sx={hasAdjustment ? { bgcolor: rowBg } : {}}>
                        {showTicketNumber && (
                          <TableCell sx={{ whiteSpace: 'nowrap', py: 0.75 }}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                              {item.ticket_number}
                            </Typography>
                          </TableCell>
                        )}
                        <TableCell sx={{ py: 0.75 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                              {item.service_name}
                            </Typography>
                            {item.is_external && (
                              <Chip
                                label="External"
                                size="small"
                                sx={{ height: 16, fontSize: '0.65rem', bgcolor: 'action.selected', color: 'secondary.main' }}
                              />
                            )}
                            {woChip(item)}
                            {feeChip(item)}
                            {lineItemsUnlocked && (
                              <Tooltip title="Edit line item">
                                <IconButton size="small" onClick={() => openCustomerEditDialog(item)} sx={{ p: 0.25 }}>
                                  <EditIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                          {item.description && (
                            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                              {item.description}
                            </Typography>
                          )}
                          {transactionNumbersOf(item).length > 0 && (
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
                              {transactionNumbersOf(item).map((number) => (
                                <Chip key={number} label={number} size="small" variant="outlined"
                                  sx={{ fontSize: '0.7rem', height: 20, fontWeight: 500 }} />
                              ))}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.75 }}>
                          <Typography sx={{ fontSize: '0.85rem' }}>{item.quantity || 1}</Typography>
                        </TableCell>
                        {showCustomerCol && (
                          <TableCell sx={{ py: 0.75 }}>
                            <Typography sx={{ fontSize: '0.85rem' }}>{item.customer_name || '-'}</Typography>
                          </TableCell>
                        )}
                        <TableCell align="right" sx={{ py: 0.75 }}>
                          <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                            {hasAdjustment ? (
                              <>
                                <Typography sx={{ fontSize: '0.85rem', color: adjColor, fontWeight: 600 }}>
                                  {adjSign}{formatCurrency(Math.abs(item.service_charge_markup))}
                                </Typography>
                                {lineItemsUnlocked && (
                                  <>
                                    <Tooltip title={`Edit ${adjLabel}`}>
                                      <IconButton size="small" onClick={() => openLineItemMarkupDialog(item)}>
                                        <EditIcon sx={{ fontSize: 16 }} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title={`Remove ${adjLabel}`}>
                                      <IconButton size="small" color="error" onClick={() => handleRemoveLineItemMarkup(item.id)}>
                                        <RemoveMarkupIcon sx={{ fontSize: 16 }} />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                )}
                              </>
                            ) : (
                              lineItemsUnlocked ? (
                                <Tooltip title="Add markup or discount">
                                  <IconButton size="small" color="success" onClick={() => openLineItemMarkupDialog(item)}>
                                    <AddIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>-</Typography>
                              )
                            )}
                          </Box>
                        </TableCell>
                        {includeTaxDetails && (
                          <TableCell align="right" sx={{ py: 0.75 }}>
                            <Typography sx={{ fontSize: '0.85rem' }}>{formatCurrency(lineFees(item))}</Typography>
                            {fineNote(item)}
                          </TableCell>
                        )}
                        <TableCell align="right" sx={{ py: 0.75 }}>
                          <Typography sx={{ fontSize: '0.85rem' }}>
                            {includeTaxDetails
                              ? formatCurrency(originalServiceCharge || 0)
                              : formatCurrency(rowCharges(item).total)
                            }
                          </Typography>
                        </TableCell>
                        {includeTaxDetails && (
                          <TableCell align="right" sx={{ py: 0.75 }}>
                            <Typography sx={{ fontSize: '0.85rem' }}>
                              {formatCurrency((hasAdjustment && (item.tax_markup ?? 0) !== 0
                                ? (item.tax_value - item.tax_markup)
                                : Number(item.tax_value || 0)) + Number(item.addon_vat || 0))}
                            </Typography>
                            {hasAdjustment && (item.tax_markup ?? 0) !== 0 && (
                              <Typography sx={{ fontSize: '0.7rem', color: adjColor }}>
                                ({item.tax_markup > 0 ? '+' : '−'}{formatCurrency(Math.abs(item.tax_markup))})
                              </Typography>
                            )}
                          </TableCell>
                        )}
                        <TableCell align="right" sx={{ py: 0.75 }}>
                          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                            {formatCurrency(rowCharges(item).total)}
                          </Typography>
                          {hasAdjustment && (
                            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                              (Base: {formatCurrency(item.original_line_total)})
                            </Typography>
                          )}
                        </TableCell>
                        {lineItemsUnlocked && (
                          <TableCell align="center" sx={{ py: 0.75, whiteSpace: 'nowrap' }}>
                            <Tooltip title="Move up">
                              <span>
                                <IconButton size="small" disabled={reordering || idx === 0} onClick={() => handleMoveLineItem(idx, -1)}>
                                  <ArrowUpIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Move down">
                              <span>
                                <IconButton size="small" disabled={reordering || idx === invoice.lineItems.length - 1} onClick={() => handleMoveLineItem(idx, 1)}>
                                  <ArrowDownIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            {(((item.is_external || item.work_order_line_item_id) && lineItemsUnlocked) || (!item.is_external && canRemoveInternal)) && (
                              <Tooltip title="Remove from invoice">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemoveLineItem(item.id)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            )}

            {/* Totals */}
            {invoice.lineItems && invoice.lineItems.length > 0 && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: isMobile ? 'center' : 'flex-end' }}>
                <Box sx={{ width: isMobile ? '100%' : 250, maxWidth: 300 }}>
                  {/* Subtotal and tax with tax details on, or with Subtotal & Tax on, as the PDF (drawTotals) */}
                  {showSubtotalAndTax && (
                    <>
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="body2">Subtotal:</Typography>
                        <Typography variant="body2">{formatCurrency(invoice.subtotal)}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="body2">Tax:</Typography>
                        <Typography variant="body2">{formatCurrency(invoice.tax_total)}</Typography>
                      </Box>
                      <Divider sx={{ my: 1 }} />
                    </>
                  )}
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant={isMobile ? 'body1' : 'body1'} fontWeight={600}>Grand Total:</Typography>
                    <Typography variant={isMobile ? 'h6' : 'body1'} fontWeight={600} color="primary">
                      {formatCurrency(invoice.grand_total)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Paper>

          {/* Notes */}
          {(invoice.notes || invoice.internal_notes) && (
            <Paper sx={{ p: 3, mt: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} mb={2}>
                Notes
              </Typography>
              {invoice.notes && (
                <Box mb={2}>
                  <Typography variant="caption" color="text.secondary">Customer Notes</Typography>
                  <Typography variant="body2">{invoice.notes}</Typography>
                </Box>
              )}
              {invoice.internal_notes && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Internal Notes</Typography>
                  <Typography variant="body2">{invoice.internal_notes}</Typography>
                </Box>
              )}
            </Paper>
          )}
        </Grid>

        {(creditNotes.length > 0 || isCredited) && (
          <Grid item xs={12}>
            <Paper sx={{ p: isMobile ? 2 : 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                <Typography variant="h6" fontWeight={600} sx={{ fontSize: isMobile ? '1rem' : '1.1rem' }}>Credit Notes</Typography>
                <Typography variant="body2" color="text.secondary">
                  Credited {formatCurrency(invoice.credited_amount || 0)} · Amount due {formatCurrency(invoice.amount_due || 0)}
                </Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Credit Note</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">Off the balance</TableCell>
                      <TableCell align="right">To the wallet</TableCell>
                      <TableCell align="right">PDF</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {creditNotes.map((cn) => (
                      <TableRow key={cn.id} hover>
                        <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{cn.creditNoteNumber}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(cn.creditNoteDate)}</TableCell>
                        <TableCell>
                          {cn.reason}
                          <Typography variant="caption" color="text.secondary" display="block">
                            {(cn.lines || []).map((l) => `${COMPONENT_LABEL[l.component] || l.component} ${formatCurrency(l.amount)}`).join(' · ')}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {formatCurrency(Number(cn.netAmount || 0) + Number(cn.vatAmount || 0))}
                        </TableCell>
                        <TableCell align="right">{formatCurrency(cn.appliedAmount || 0)}</TableCell>
                        <TableCell align="right">{formatCurrency(cn.walletAmount || 0)}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Open the tax credit note">
                            <IconButton size="small" onClick={() => invoiceMoneyService.openCreditNotePdf(invoiceId, cn.id)
                              .catch((err) => setError(serverMessage(err, 'Failed to open the credit note')))}>
                              <ViewPdfIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        )}

        {/* ===== Customer Payment History ===== */}
        <Grid item xs={12}>
          <Paper sx={{ p: isMobile ? 2 : 3, mt: 0 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
              <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                <Typography variant="h6" fontWeight={600} sx={{ fontSize: isMobile ? '1rem' : '1.1rem' }}>
                  Customer Payment History
                </Typography>
                {selectedReceiptIds.length > 0 && canWriteFin && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<VoidIcon />}
                    onClick={openBulkVoidDialog}
                  >
                    Void Selected ({selectedReceiptIds.length})
                  </Button>
                )}
              </Box>
              <Box display="flex" gap={3} flexWrap="wrap">
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Paid
                  </Typography>
                  <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: 'success.main' }}>
                    {formatCurrency(invoice.paid_amount || 0)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Outstanding
                  </Typography>
                  <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: (invoice.amount_due || 0) > 0 ? 'error.main' : 'text.secondary' }}>
                    {formatCurrency(invoice.amount_due || 0)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Status
                  </Typography>
                  <Chip
                    size="small"
                    label={invoice.customer_payment_status || 'UNPAID'}
                    color={
                      invoice.customer_payment_status === 'PAID' ? 'success' :
                      invoice.customer_payment_status === 'PARTIAL' ? 'warning' :
                      'default'
                    }
                  />
                </Box>
              </Box>
            </Box>

            {loadingReceipts ? (
              <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress size={24} />
              </Box>
            ) : paymentReceipts.length === 0 ? (
              <Box py={3} textAlign="center">
                <Typography variant="body2" color="text.secondary">
                  No payments yet.
                </Typography>
              </Box>
            ) : (() => {
              const activeReceipts = paymentReceipts.filter((r) => r.status !== 'VOIDED');
              const allActiveSelected = activeReceipts.length > 0 && selectedReceiptIds.length === activeReceipts.length;
              const someActiveSelected = selectedReceiptIds.length > 0 && selectedReceiptIds.length < activeReceipts.length;
              return (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          disabled={activeReceipts.length === 0}
                          checked={allActiveSelected}
                          indeterminate={someActiveSelected}
                          onChange={(e) => toggleSelectAllActiveReceipts(e.target.checked)}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>Receipt #</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>Date</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>Amount</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>Initiator</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>Payment</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paymentReceipts.map((r) => {
                      const isVoided = r.status === 'VOIDED';
                      const isSelected = selectedReceiptIds.includes(r.id);
                      return (
                        <TableRow key={r.id} hover selected={isSelected} sx={{ opacity: isVoided ? 0.65 : 1 }}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              disabled={isVoided}
                              checked={isSelected}
                              onChange={() => toggleReceiptSelection(r.id)}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 1 }}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: 'secondary.main', cursor: 'pointer' }}
                              onClick={() => navigate(`/receipts/${r.id}`)}>
                              {r.receiptNumber}
                            </Typography>
                            {isVoided && r.voidReason && (
                              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontStyle: 'italic' }}>
                                {r.voidReason}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8125rem', py: 1, color: 'text.secondary' }}>
                            {formatDate(r.createdAt)}
                          </TableCell>
                          <TableCell align="right" sx={{ py: 1 }}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: isVoided ? 'text.secondary' : 'success.main', textDecoration: isVoided ? 'line-through' : 'none' }}>
                              {formatCurrency(r.amount)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ py: 1 }}>
                            {r.initiator ? (
                              <Chip
                                size="small"
                                label={r.initiator === 'CUSTOMER' ? 'Customer' : 'Typing Center'}
                                color={r.initiator === 'CUSTOMER' ? 'primary' : 'warning'}
                                sx={{ fontSize: '0.7rem', height: 22 }}
                              />
                            ) : '—'}
                          </TableCell>
                          <TableCell sx={{ py: 1 }}>
                            {r.paymentNumber ? (
                              r.initiator === 'TC' ? (
                                <Typography sx={{ fontSize: '0.85rem', color: 'info.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                  onClick={() => navigate(`/settlements/${r.paymentId}`)}>
                                  {r.paymentNumber}
                                </Typography>
                              ) : (
                                <Typography sx={{ fontSize: '0.85rem' }}>{r.paymentNumber}</Typography>
                              )
                            ) : '—'}
                          </TableCell>
                          <TableCell align="center" sx={{ py: 1 }}>
                            <Chip
                              size="small"
                              label={r.status}
                              color={r.status === 'VOIDED' ? 'error' : 'success'}
                              sx={{ fontSize: '0.7rem', height: 22 }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              );
            })()}
          </Paper>
        </Grid>
      </Grid>

      {/* Add Tickets Dialog */}
      <Dialog
        open={ticketDialogOpen}
        onClose={() => setTicketDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Tickets to Invoice</DialogTitle>
        <DialogContent>
          {loadingTickets ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : availableTickets.length === 0 ? (
            <Box py={4} textAlign="center">
              <Typography color="text.secondary">
                No available tickets found for this organization.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                All completed tickets may already be invoiced.
              </Typography>
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Select tickets to add to this invoice. Only completed tickets that haven't been invoiced are shown.
              </Typography>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ '& th': { bgcolor: 'action.hover', color: 'text.primary', fontWeight: 600, fontSize: '0.85rem' } }}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selectedTickets.length === availableTickets.length}
                          indeterminate={selectedTickets.length > 0 && selectedTickets.length < availableTickets.length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedTickets(availableTickets.map(t => t.id));
                            } else {
                              setSelectedTickets([]);
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Ticket #</TableCell>
                      <TableCell>Service</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {availableTickets.map((ticket) => (
                      <TableRow
                        key={ticket.id}
                        hover
                        onClick={() => {
                          if (selectedTickets.includes(ticket.id)) {
                            setSelectedTickets(prev => prev.filter(id => id !== ticket.id));
                          } else {
                            setSelectedTickets(prev => [...prev, ticket.id]);
                          }
                        }}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell padding="checkbox" sx={{ py: 1 }}>
                          <Checkbox size="small" checked={selectedTickets.includes(ticket.id)} />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', py: 1 }}>
                          <Typography sx={{ fontSize: '0.85rem' }}>
                            {ticket.orgTicketNumber || ticket.org_ticket_number || ticket.ticketNumber || ticket.ticket_number}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography sx={{ fontSize: '0.85rem' }}>{ticket.serviceName || ticket.service_name}</Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography sx={{ fontSize: '0.85rem' }}>{ticket.customerName || ticket.customer_name}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1 }}>
                          <Typography sx={{ fontSize: '0.85rem' }}>
                            {formatCurrency(ticket.totalCost || ticket.total_cost)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography variant="body2" color="primary" sx={{ mt: 2 }}>
                {selectedTickets.length} ticket(s) selected
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTicketDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddTickets}
            disabled={addingTickets || selectedTickets.length === 0}
            startIcon={addingTickets && <CircularProgress size={20} />}
          >
            {addingTickets ? 'Adding...' : `Add ${selectedTickets.length} Ticket(s)`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Void Dialog */}
      <Dialog open={voidDialogOpen} onClose={() => setVoidDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Void Invoice</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Are you sure you want to void this invoice? This action cannot be undone.
          </Typography>
          <TextField
            label="Reason for voiding"
            value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)}
            fullWidth
            multiline
            rows={3}
            required
          />
          {paidFees().length > 0 && (
            <Box mt={2}>
              <Typography fontWeight={600} color="primary.main" mb={1}>Paid government fees — what happened to the money?</Typography>
              <PaidItemOutcomes payments={paidFees()} outcomes={outcomes} setOutcomes={setOutcomes} accounts={tcAccounts} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoidDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleVoid}
            disabled={voiding || !voidReason.trim() || !outcomesComplete(paidFees(), outcomes)}
            startIcon={voiding && <CircularProgress size={20} />}
          >
            {voiding ? 'Voiding...' : 'Void Invoice'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Void Receipts Dialog */}
      <Dialog open={bulkVoidDialogOpen} onClose={() => !bulkVoidProcessing && setBulkVoidDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Void {selectedReceiptIds.length} Receipt{selectedReceiptIds.length === 1 ? '' : 's'}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Voiding cannot be undone. The total amount of each voided receipt
            will be refunded to the customer's wallet, and this invoice's
            paid amount and customer payment status will be recomputed.
          </Alert>
          {(() => {
            const selected = paymentReceipts.filter((r) => selectedReceiptIds.includes(r.id));
            const total = selected.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
            return (
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  The following receipts will be voided:
                </Typography>
                <Box sx={{ maxHeight: 180, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                  {selected.map((r) => (
                    <Box key={r.id} display="flex" justifyContent="space-between" sx={{ py: 0.25 }}>
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{r.receiptNumber}</Typography>
                      <Typography sx={{ fontSize: '0.85rem', color: 'success.main', fontWeight: 600 }}>
                        {formatCurrency(r.amount)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Box display="flex" justifyContent="space-between" mt={1}>
                  <Typography variant="body2" fontWeight={600}>Total to refund</Typography>
                  <Typography variant="body2" fontWeight={700} color="error.main">
                    {formatCurrency(total)}
                  </Typography>
                </Box>
              </Box>
            );
          })()}
          <TextField
            label="Reason for voiding"
            value={bulkVoidReason}
            onChange={(e) => setBulkVoidReason(e.target.value)}
            fullWidth
            multiline
            rows={3}
            required
            disabled={bulkVoidProcessing}
            helperText="Applied to every selected receipt."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkVoidDialogOpen(false)} disabled={bulkVoidProcessing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleBulkVoidSubmit}
            disabled={bulkVoidProcessing || !bulkVoidReason.trim()}
            startIcon={bulkVoidProcessing && <CircularProgress size={20} />}
          >
            {bulkVoidProcessing
              ? 'Voiding...'
              : `Void ${selectedReceiptIds.length} Receipt${selectedReceiptIds.length === 1 ? '' : 's'}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mark-Paid dialog removed — see header comment above the
          "Customer payment is now driven by the Receipts flow" block. */}

      {/* Line Item Markup / Discount Dialog */}
      <Dialog open={lineItemMarkupDialogOpen} onClose={() => setLineItemMarkupDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            {markupDialogTab === 'discount'
              ? <DiscountIcon color="error" />
              : <MarkupIcon color="success" />}
            {markupDialogTab === 'discount' ? 'Apply Discount to Ticket' : 'Apply Markup to Ticket'}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedLineItem && (() => {
            const isDiscount = markupDialogTab === 'discount';
            const isInternal = !selectedLineItem.is_external;
            const sign = isDiscount ? -1 : 1;
            const inputInclTax = parseFloat(lineItemMarkupAmount) || 0;
            const signedInclTax = sign * inputInclTax;
            const signedExclTax = signedInclTax / 1.05;
            const taxOnSigned = signedInclTax - signedExclTax;
            const originalService = parseFloat(selectedLineItem.original_service_charge || selectedLineItem.service_charge) || 0;
            const originalTax = parseFloat(selectedLineItem.original_tax_value || selectedLineItem.tax_value) || 0;
            const newService = originalService + signedExclTax;
            const newTax = originalTax + taxOnSigned;
            const previewColor = isDiscount ? 'error.main' : 'success.main';
            const previewBg = isDiscount
              ? ((theme) => theme.palette.mode === 'dark' ? 'rgba(211,47,47,0.10)' : '#fef2f2')
              : ((theme) => theme.palette.mode === 'dark' ? 'rgba(46,125,50,0.10)' : '#f0fdf4');
            const signPrefix = signedInclTax === 0 ? '' : (signedInclTax > 0 ? '+' : '−');
            const fmtSigned = (n) => `${signPrefix}${formatCurrency(Math.abs(n))}`;

            return (
              <>
                <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>{selectedLineItem.ticket_number}</Typography>
                  <Typography variant="body2" color="text.secondary">{selectedLineItem.service_name}</Typography>
                </Box>

                <Tabs
                  value={markupDialogTab}
                  onChange={(_, v) => setMarkupDialogTab(v)}
                  sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
                  textColor="inherit"
                >
                  <Tab value="markup" icon={<MarkupIcon fontSize="small" />} iconPosition="start" label="Markup" sx={{ minHeight: 40 }} />
                  <Tab value="discount" icon={<DiscountIcon fontSize="small" />} iconPosition="start" label="Discount" sx={{ minHeight: 40 }} />
                </Tabs>

                {isDiscount && isInternal && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    This is an <strong>internal</strong> line item. A discount here is absorbed by the typing center as a loss — the full Thaklis cost is still owed.
                  </Alert>
                )}

                <Box sx={{ bgcolor: previewBg, p: 2, borderRadius: 1, mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    SERVICE CHARGE
                  </Typography>
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2" color="text.secondary">Original:</Typography>
                    <Typography variant="body2">{formatCurrency(originalService)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="text.secondary">
                      {isDiscount ? '− Discount:' : '+ Markup:'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: previewColor }}>{fmtSigned(signedExclTax)}</Typography>
                  </Box>

                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    TAX (5% VAT)
                  </Typography>
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2" color="text.secondary">Original:</Typography>
                    <Typography variant="body2">{formatCurrency(originalTax)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="text.secondary">
                      {isDiscount ? '− Tax on Discount (5%):' : '+ Tax on Markup (5%):'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: previewColor }}>{fmtSigned(taxOnSigned)}</Typography>
                  </Box>

                  <Divider sx={{ my: 1 }} />
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2" fontWeight={600}>
                      {isDiscount ? 'Total Discount (inc. tax):' : 'Total Markup (inc. tax):'}
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ color: previewColor }}>
                      {fmtSigned(signedInclTax)}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">New Service Charge:</Typography>
                    <Typography variant="body2">{formatCurrency(newService)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">New Tax:</Typography>
                    <Typography variant="body2">{formatCurrency(newTax)}</Typography>
                  </Box>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label={isDiscount ? 'Discount (Incl. Tax)' : 'Markup (Incl. Tax)'}
                      value={lineItemMarkupAmount}
                      onChange={(e) => setLineItemMarkupAmount(e.target.value)}
                      fullWidth
                      type="number"
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 1 }}>AED</Typography>
                      }}
                      helperText={isDiscount
                        ? 'Enter the discount amount to subtract from this ticket'
                        : 'Enter the amount to add as your markup on this ticket'}
                      autoFocus
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label={isDiscount ? 'Discount (Excl. Tax)' : 'Markup (Excl. Tax)'}
                      value={inputInclTax ? (inputInclTax / 1.05).toFixed(2) : ''}
                      fullWidth
                      disabled
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 1 }}>AED</Typography>
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Tax"
                      value={inputInclTax ? (inputInclTax - inputInclTax / 1.05).toFixed(2) : ''}
                      fullWidth
                      disabled
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 1 }}>AED</Typography>
                      }}
                    />
                  </Grid>
                </Grid>
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLineItemMarkupDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color={markupDialogTab === 'discount' ? 'error' : 'success'}
            onClick={handleApplyLineItemMarkup}
            disabled={applyingLineItemMarkup || !lineItemMarkupAmount || parseFloat(lineItemMarkupAmount) <= 0}
            startIcon={applyingLineItemMarkup
              ? <CircularProgress size={20} />
              : (markupDialogTab === 'discount' ? <DiscountIcon /> : <AddIcon />)}
          >
            {applyingLineItemMarkup
              ? 'Applying...'
              : (markupDialogTab === 'discount' ? 'Apply Discount' : 'Apply Markup')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Return to Draft confirmation (FINAL invoices) */}
      <Dialog open={returnOpen} onClose={() => setReturnOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Return to Draft — {invoice.invoice_number}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            The invoice goes back to draft with the same number so you can correct it and finalise it again. Its
            finalisation is reversed in the books.
          </Typography>
          {emailHistory.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This invoice was already emailed to the customer. If it was sent, a credit note may be the better correction.
            </Alert>
          )}
          <TextField label="Reason *" fullWidth size="small" value={returnReason} onChange={(e) => setReturnReason(e.target.value)}
            placeholder="e.g. Wrong service charge" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReturnOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" startIcon={<ReturnDraftIcon />} disabled={returning || !returnReason.trim()}
            onClick={handleReturnToDraft}>
            Return to Draft
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(removeTarget)} onClose={() => setRemoveTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Remove Line</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            This line&apos;s government fee was paid. Say what happened to the money before removing it.
          </Typography>
          {removeTarget && (
            <PaidItemOutcomes payments={paidFees(removeTarget)} outcomes={outcomes} setOutcomes={setOutcomes} accounts={tcAccounts} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveTarget(null)}>Keep Line</Button>
          <Button variant="contained" color="error" disabled={!removeTarget || !outcomesComplete(paidFees(removeTarget), outcomes)}
            onClick={() => handleRemoveLineItem(removeTarget, true)}>
            Remove Line
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Draft — {invoice.invoice_number}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={paidFees().length ? 2 : 0}>
            The draft and its lines are deleted. Tickets and work orders on it can be invoiced again.
          </Typography>
          {paidFees().length > 0 && (
            <>
              <Typography fontWeight={600} color="primary.main" mb={1}>Paid government fees — what happened to the money?</Typography>
              <PaidItemOutcomes payments={paidFees()} outcomes={outcomes} setOutcomes={setOutcomes} accounts={tcAccounts} />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Keep Draft</Button>
          <Button variant="contained" color="error" disabled={deleting || !outcomesComplete(paidFees(), outcomes)} onClick={handleDeleteDraft}>
            Delete Draft
          </Button>
        </DialogActions>
      </Dialog>

      <InvoiceFeeDialog
        open={Boolean(feeLineId)}
        onClose={() => setFeeLineId(null)}
        invoice={invoice}
        line={(invoice.lineItems || []).find((l) => l.id === feeLineId)}
        payment={feeLineId ? feePaymentOf(feeLineId) : null}
        editable={isDraft && canWriteInv}
        canFinance={canWriteFin}
        onChanged={refreshMoney}
      />

      <CreditNoteDialog
        open={creditOpen}
        onClose={() => setCreditOpen(false)}
        invoice={invoice}
        creditNotes={creditNotes}
        prefill={creditPrefill}
        onIssued={(note) => {
          setCreditOpen(false);
          setSuccess(`Credit note ${note.creditNoteNumber} issued — ${formatCurrency(note.appliedAmount || 0)} off the balance`
            + (Number(note.walletAmount) > 0 ? `, ${formatCurrency(note.walletAmount)} to the customer's wallet` : ''));
          fetchInvoice();
          loadMoney();
        }}
      />

      {/* Edit Line Item Dialog */}
      <Dialog open={customerEditOpen} onClose={() => setCustomerEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Line Item</DialogTitle>
        <DialogContent>
          {customerEditItem && (
            <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {customerEditItem.ticket_number} · {customerEditItem.service_name}
              </Typography>
              <TextField
                autoFocus
                fullWidth
                label="Customer Name"
                value={customerEditValue}
                onChange={(e) => setCustomerEditValue(e.target.value)}
                size="small"
              />
              <TextField
                fullWidth
                label="Description"
                placeholder="Shown under the service name"
                value={descriptionEditValue}
                onChange={(e) => setDescriptionEditValue(e.target.value)}
                size="small"
                multiline
                minRows={1}
              />
              {customerEditItem.is_external && !customerEditItem.work_order_line_item_id && (
                <>
                  <TextField
                    fullWidth
                    label="Transaction Number"
                    placeholder="e.g., GDRFA/ICP transaction number"
                    helperText="Up to 3, separated by commas"
                    value={transactionNumberEditValue}
                    onChange={(e) => setTransactionNumberEditValue(e.target.value)}
                    size="small"
                  />
                  <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>Financial Details</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Quantity"
                        type="number"
                        value={quantityEditValue}
                        onChange={(e) => setQuantityEditValue(e.target.value)}
                        inputProps={{ min: 1, step: 1 }}
                        helperText="Whole number, min 1 — charges below are per unit"
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Government Charge (AED)"
                        value={govtChargeEdit}
                        onChange={(e) => setGovtChargeEdit(e.target.value)}
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Service Charge Incl. Tax (AED)"
                        value={serviceChargeInclEdit}
                        onChange={(e) => {
                          const value = e.target.value;
                          setServiceChargeInclEdit(value);
                          const incl = parseFloat(value);
                          if (!Number.isFinite(incl) || incl <= 0) {
                            setServiceChargeExclEdit('');
                            setTaxEdit('');
                            return;
                          }
                          const excl = Math.round((incl / 1.05) * 100) / 100;
                          const tax = Math.round((incl - excl) * 100) / 100;
                          setServiceChargeExclEdit(excl.toFixed(2));
                          setTaxEdit(tax.toFixed(2));
                        }}
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Service Charge Excl. Tax (AED)"
                        value={serviceChargeExclEdit}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Tax (AED)"
                        value={taxEdit}
                        disabled
                      />
                    </Grid>
                  </Grid>
                  <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 1, border: '1px solid #e2e8f0' }}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" fontWeight={600}>Total:</Typography>
                      <Typography variant="body2" fontWeight={600} color="primary">
                        AED {(((parseFloat(govtChargeEdit) || 0) + (parseFloat(serviceChargeInclEdit) || 0)) * Math.max(1, parseInt(quantityEditValue, 10) || 1)).toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    * Service charge includes 5% VAT
                  </Typography>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomerEditOpen(false)} disabled={savingCustomerName}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveCustomerName}
            disabled={savingCustomerName}
            startIcon={savingCustomerName ? <CircularProgress size={16} /> : null}
          >
            {savingCustomerName ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add External Service Dialog */}
      <AddExternalServiceDialog
        open={externalServiceDialogOpen}
        onClose={() => setExternalServiceDialogOpen(false)}
        invoiceId={invoiceId}
        onSuccess={handleExternalServiceSuccess}
        thaklisDisabled={thaklisDisabled}
        organizationId={invoice?.internal_organization_id || invoice?.internalOrganizationId || null}
      />

      {/* Send Email Dialog */}
      <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <EmailIcon color="primary" />
            Send Invoice via Email
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1, mb: 2 }}>
            <Typography variant="subtitle2">{invoice?.invoice_number}</Typography>
            <Typography variant="body2" color="text.secondary">{invoice?.internal_organization_name || '—'}</Typography>
            <Typography variant="body2" color="primary" fontWeight={600}>
              {formatCurrency(invoice?.grand_total)}
            </Typography>
          </Box>

          <Typography variant="subtitle2" mb={1}>Recipients *</Typography>
          <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
            {emailRecipients.map((email, index) => (
              <Chip
                key={index}
                label={email}
                onDelete={() => handleRemoveRecipient(email)}
                size="small"
              />
            ))}
            <Chip
              icon={<AddIcon />}
              label="Add Recipient"
              onClick={handleAddRecipient}
              variant="outlined"
              size="small"
              sx={{ cursor: 'pointer' }}
            />
          </Box>

          <TextField
            label="CC Recipients (comma-separated)"
            value={emailCc}
            onChange={(e) => setEmailCc(e.target.value)}
            fullWidth
            size="small"
            placeholder="email1@example.com, email2@example.com"
            helperText="Optional: Add CC recipients separated by commas"
            sx={{ mb: 2 }}
          />

          {/* Email History */}
          {emailHistory.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" mb={1} display="flex" alignItems="center" gap={1}>
                <HistoryIcon sx={{ fontSize: 16 }} />
                Email History
              </Typography>
              <Box sx={{ maxHeight: 150, overflowY: 'auto', bgcolor: 'action.hover', borderRadius: 1, p: 1 }}>
                {emailHistory.map((log, index) => (
                  <Box key={index} sx={{ mb: 1, pb: 1, borderBottom: index < emailHistory.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        {new Date(log.sent_at || log.created_at).toLocaleString()}
                      </Typography>
                      <Chip
                        label={log.status}
                        size="small"
                        color={log.status === 'SENT' ? 'success' : log.status === 'FAILED' ? 'error' : 'default'}
                        sx={{ fontSize: '0.65rem', height: 18 }}
                      />
                    </Box>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      To: {Array.isArray(log.to_emails) ? log.to_emails.join(', ') : log.to_emails}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {loadingEmailHistory && (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={20} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSendEmail}
            disabled={sendingEmail || emailRecipients.length === 0}
            startIcon={sendingEmail ? <CircularProgress size={20} /> : <SendIcon />}
          >
            {sendingEmail ? 'Sending...' : 'Send Invoice'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Invoice Dialog */}
      <Dialog open={updateDialogOpen} onClose={closeUpdateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{isDraft ? 'Update Invoice' : 'Edit Invoice'}</DialogTitle>
        <DialogContent>
          <Box mt={1} display="flex" flexDirection="column" gap={2.5}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Invoice Date"
                value={updateInvoiceDateInput}
                onChange={(newValue) => setUpdateInvoiceDateInput(newValue)}
                inputFormat="DD/MM/YYYY"
                disabled={savingUpdate || isFinal}
                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
              />
            </LocalizationProvider>

            <TextField
              label="Subject"
              value={updateSubjectInput}
              onChange={(e) => setUpdateSubjectInput(e.target.value)}
              disabled={savingUpdate}
              fullWidth
              size="small"
              placeholder="e.g. CANCELLATION - JOAN & JOANNA"
              helperText="Shown above the line items on the invoice PDF"
            />

            {isFinal && (
              <Typography variant="caption" color="text.secondary">
                A final invoice keeps its date and customer. Return it to draft to change them.
              </Typography>
            )}
            {isEditable && !isFinal && ['TYPING_CENTER', 'B2B'].includes(typingCenterOrgType) && (
              <Autocomplete
                fullWidth
                size="small"
                disabled={savingUpdate}
                loading={loadingInternalOrgs}
                options={internalOrganizations}
                value={selectedInternalOrg}
                onInputChange={(e, value, reason) => {
                  // Only typed/cleared text drives the backend search; MUI's own
                  // 'reset' (e.g. after selecting/prefill) must not trigger a refetch.
                  if (reason === 'input' || reason === 'clear') setInternalOrgSearch(value);
                }}
                onChange={(e, value) => {
                  setSelectedInternalOrg(value);
                  setSelectedInternalOrgId(value?.id || '');
                }}
                getOptionLabel={(o) => o?.name || ''}
                isOptionEqualToValue={(o, v) => o?.id === v?.id}
                filterOptions={(x) => x}  // server-side search; don't re-filter locally
                noOptionsText={loadingInternalOrgs ? 'Loading…' : 'No organizations found'}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Customer Organization"
                    placeholder="Search by name…"
                  />
                )}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeUpdateDialog} disabled={savingUpdate}>Cancel</Button>
          <Button
            onClick={handleSaveUpdate}
            variant="contained"
            disabled={savingUpdate}
            startIcon={savingUpdate ? <CircularProgress size={16} /> : null}
          >
            {savingUpdate ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvoiceDetail;
